import { PlainButton } from "@medoma/components/Button/Button";
import { useParentRoutesPatient } from "@/api/Patients";
import styles from "./CareEpisode.module.scss";
import {
  canDischargePatient,
  declinedCategoryDictionary,
  getStatusTimestamp,
  patientStatusDictionary,
  patientStatusSchema,
  type IExistingPatient,
} from "@medoma/models/patients";
import { Text } from "@medoma/components/Text/Text";
import { Trans, useLingui } from "@lingui/react/macro";
import PausedChip from "@/components/Chips/PausedChip";
import { humanFormat } from "@medoma/models/date-and-time";
import { AllocatedAssets } from "../Equipment/AllocatedAssets";
import { Heading } from "@medoma/components/Heading/Heading";
import { useQuery } from "@tanstack/react-query";
import { careEpisodeAssetsQueryOptions } from "@/api/Equipment";
import { logisticsConfigQueryOptions } from "@/api/Config";

const Timestamp = ({ patient }: { patient: IExistingPatient }) => {
  const timestamp = getStatusTimestamp(patient);
  if (!timestamp) {
    return null;
  }

  return (
    <Text element="span">
      {humanFormat(timestamp, { dateStyle: "medium", timeStyle: "short" })}
    </Text>
  );
};

const AllocatedEquipmentSection = ({ patientId }: { patientId: string }) => {
  const { data: assets } = useQuery(
    careEpisodeAssetsQueryOptions({ careEpisodeId: patientId }),
  );

  if (!assets || assets.length === 0) {
    return null;
  }

  return (
    <div className={styles.allocatedEquipment}>
      <Heading level="h2" size="base" weight="bold">
        <Trans>Tilldelad utrustning</Trans>
      </Heading>
      <AllocatedAssets patientId={patientId} />
    </div>
  );
};

const CareEpisode = () => {
  const patient = useParentRoutesPatient();
  const { data: equipmentManagementEnabled } = useQuery({
    ...logisticsConfigQueryOptions(),
    select: (data) => data.equipmentManagement.enabled,
  });
  const { t } = useLingui();

  const isPaused =
    patient.status === patientStatusSchema.enum.admitted && patient.pausedAt;

  const patientStatusAllowsPause =
    patient.status === patientStatusSchema.enum.admitted;

  return (
    <section className={styles.careEpisode}>
      <div className={styles.statusAndTimestamp}>
        <Text element="span" weight="bold">
          {t(patientStatusDictionary[patient.status].singular)}
        </Text>
        {patient.status === patientStatusSchema.enum.declined &&
        patient.declinedCategory ? (
          <>
            <div className={styles.separator} />
            <Text element="span" weight="bold">
              {t(declinedCategoryDictionary[patient.declinedCategory])}
            </Text>
          </>
        ) : null}
        <Timestamp patient={patient} />
      </div>
      {isPaused && patient.pausedAt ? (
        <PausedChip pausedAt={patient.pausedAt} />
      ) : null}
      {equipmentManagementEnabled ? (
        <AllocatedEquipmentSection patientId={patient.id} />
      ) : null}
      {canDischargePatient({ status: patient.status }) ? (
        <div className={styles.careEpisodeActions}>
          <PlainButton asLinkTo="discharge">{t`Skriv ut`}</PlainButton>
          {patientStatusAllowsPause ? (
            isPaused ? (
              <PlainButton asLinkTo="unpause">{t`Återuppta`}</PlainButton>
            ) : (
              <PlainButton asLinkTo="pause">{t`Pausa`}</PlainButton>
            )
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export { CareEpisode };
